import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import cx from "classnames";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";

import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { Add as AddIcon } from '@vedmarket/ui-kit';
import { CheckCircle as CheckCircleIcon } from '@vedmarket/ui-kit';
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { Delete as DeleteIcon } from '@vedmarket/ui-kit';
import { Fade, Popper } from "@mui/material";

import {COLORS} from "../../../../../../../config/constants";

import {
  setUserExtraServicesIds,
  useExtraServicesId,
  useLazyGetExtraServicesByOrderIdQuery,
  useRemoveExtraServicesByOrderIdMutation,
} from "../../../../../../../api/calc";

import './additional-company-card-service-item.scss';
import {useGetCalcIdFromRoute} from "../../../../../../../utils/hooks";
import {useDemoAdditionalServiceItem} from "components/cabinet/demo/steps/additional-services/use-additional-services";
import {isCalculationViewModeError, numberWithSpaces} from 'utils/helpers';

import LockedIconButton from 'components/common/locked-icon-button';
import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../calculation-read-only';


type CompanyCardServiceItemProps = {
    isActive?: boolean;
    service: ExtraServiceItem;
    isMainPage?: boolean;
    index: number;
    refetchGetExtraServices?(): void;
}

const CompanyCardServiceItem = ({ isActive = false, service, index, isMainPage } : CompanyCardServiceItemProps ) => {

  const { t } = useTranslation()
  const readOnly = useCalculationReadOnly()

  const [ isChecked, setIsChecked ] = useState(isActive);
  const [ anchorEl, setAnchorEl ] = useState<HTMLElement | null>(null);

  const dispatch = useDispatch()

  useEffect(() => {
    setIsChecked(isActive);
  }, [ isActive ]);

  const userServices = useExtraServicesId();

  const orderId = useGetCalcIdFromRoute()

  const [ removeExtraServices ] = useRemoveExtraServicesByOrderIdMutation()
  const [ triggerGetExtraServicesRequest ] = useLazyGetExtraServicesByOrderIdQuery()

  const demoPlayback = useDemoAdditionalServiceItem(service, isChecked)

  useEffect(() => {
    if (!anchorEl) return;

    const timer = setTimeout(() => {
      setAnchorEl(null);
    }, 2000);

    return () => clearTimeout(timer);
  }, [ anchorEl ]);

  const handleRemove = async () => {
    const result = await removeExtraServices({
      orderId: orderId,
      service_id: [ service.id ]
    })

    if (isCalculationViewModeError(result)) {
      return;
    }

    await triggerGetExtraServicesRequest(orderId);
  }

  const handleChangeCheckbox = (e: React.MouseEvent<HTMLButtonElement>) => {

    setAnchorEl(e.currentTarget);

    const hasId = userServices?.includes(service.id);

    let nextIds;

    if (userServices) {
      nextIds = hasId
        ? userServices?.filter(id => id !== service.id)
        : [ ...userServices, service.id ];
    } else {
      nextIds = [ service.id ]
    }

    dispatch(setUserExtraServicesIds(nextIds));
    setIsChecked(!hasId);
  };

  const renderPriceAction = () => {
    if (isMainPage) {
      return (
        <Typography
          variant='body2'
          component='div'
          sx={{
            color: COLORS.BLACK_DARK,
            fontSize: '14px',
            lineHeight: '21px',
            letterSpacing: 0,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {numberWithSpaces(service.price)} USD
          {readOnly ? (
            <LockedIconButton
              sx={{
                padding: '0 4px',
                marginLeft: '4px'
              }}
            >
              <DeleteIcon />
            </LockedIconButton>
          ) : (
            <IconButton
              onClick={handleRemove}
              sx={{
                padding: '0 4px',
                marginLeft: '4px'
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Typography>
      );
    }

    return (
      <Button
        {...demoPlayback.buttonProps}
        variant='outlined'
        className={cx('add-product__addFieldButton', { 'read-only-action-button': readOnly })}
        sx={{
          minWidth: 108,
          minHeight: 36
        }}
        disabled={readOnly}
        onClick={readOnly ? undefined : handleChangeCheckbox}
        title={readOnly ? t('calc__message__lockedByApplication') : undefined}
        startIcon={isChecked ? <CheckCircleIcon /> : <AddIcon />}
        endIcon={readOnlyButtonEndIcon(readOnly)}
      >
        <Typography component='span' variant='inherit'>{`${numberWithSpaces(service.price)} ${service.currency}`}</Typography>
        <Popper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement='right'
          modifiers={[ {
            name: 'offset',
            options: {
              offset: [ 0, 24 ],
            },
          } ]}
          transition
          disablePortal
          sx={{
            zIndex: 2,
          }}
        >
          {({ TransitionProps }) => (
            <Fade {...TransitionProps} timeout={500}>
              <Typography
                variant='body2'
                className={cx('service-popover__content', {
                  'service-popover__content_excluded': !isChecked,
                })}
                sx={{
                  color: COLORS.WHITE,
                  lineHeight: 1.6,
                  textTransform: 'none',
                  letterSpacing: 0
                }}
              >
                {formatReferenceCopy(isChecked
                  ? t('calc__text__additional__serviceAdded')
                  : t('calc__text__additional__serviceExcluded'))}
              </Typography>
            </Fade>
          )}
        </Popper>
      </Button>
    );
  };

  return <>
    <Divider sx={{marginTop: 0}}/>
    <div
      className={cx('service-item__inactive', {
        'service-item__inactive_checked': isChecked,
      })}
      {...demoPlayback.itemProps}
    >
      <Typography
        variant='body2'
        sx={{
          color: COLORS.BLACK_DARK,
          fontSize: '14px',
          lineHeight: '21px',
          letterSpacing: 0,
          minWidth: 0,
          flex: 1,
        }}
      >
        {isMainPage ? '' : `${index + 1}. `}{formatReferenceCopy(service.name)}
        {service.price_description && <Typography component='span' variant='inherit' sx={{color: COLORS.LIGHT_TEXT_DISABLED_OPACITY}}>{formatReferenceCopy(` (${service.price_description})`)}</Typography>}
      </Typography>
      {renderPriceAction()}
    </div>
  </>
}

export default CompanyCardServiceItem
